// PublicAIChat — patient-facing AI assistant served at /:hospitalId/ai.
//
// Runs in the same public surface as PublicBooking: detected via
// isPublicBookingHost() (book.doctrue.in or ?public=1), hospital_id parsed
// from the path, no auth. Mobile-first.
//
// On every load/reload it fires ONE auto-highlights request (a fresh
// AI-generated hospital intro) — rendered as the first assistant bubble with
// no user bubble, and excluded from conversation history.
//
// See memories: project_public_booking_auth_redirect (UserContext guard),
// project_public_booking_* for the public surface conventions.

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useParams } from "react-router-dom";
import {
  streamPublicAiAsk,
  AiAskError,
  AiTurn,
} from "../lib/apis/publicAi";
import { getPublicHospital, HospitalBranding } from "../lib/apis/publicBooking";

const INTRO_MESSAGE =
  "Give me a short, friendly overview of this hospital — key specialties, a couple of notable doctors, and the timings.";
const INTRO_SENTINEL = "__intro__";

const SUGGESTED_CHIPS = [
  "Our doctors",
  "Timings & location",
  "Services offered",
  "Do you accept insurance?",
];

const RATE_LIMIT_TEXT =
  "You've reached the question limit for now — please try again later or book an appointment.";

const HISTORY_TURNS = 6;

type MessageStatus = "streaming" | "done" | "error" | "rate_limited";

interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  status: MessageStatus;
  /** Auto-highlights intro is not part of conversation history. */
  excludeFromHistory?: boolean;
  /** The message to re-send if this assistant bubble is retried. */
  retryMessage?: string;
}

// ── Minimal, dependency-free markdown renderer ───────────────────────────────
// Renders **bold**, *italic*, `code`, [text](url), bullet/numbered lists and
// headings as React nodes (no dangerouslySetInnerHTML → no XSS surface).
const renderInline = (text: string, kp: string): React.ReactNode[] => {
  const nodes: React.ReactNode[] = [];
  const re =
    /(\*\*([^*]+)\*\*|\*([^*]+)\*|`([^`]+)`|\[([^\]]+)\]\(([^)]+)\))/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    if (m[2] != null) nodes.push(<strong key={`${kp}b${i}`}>{m[2]}</strong>);
    else if (m[3] != null) nodes.push(<em key={`${kp}i${i}`}>{m[3]}</em>);
    else if (m[4] != null)
      nodes.push(
        <code
          key={`${kp}c${i}`}
          className="px-1 py-0.5 rounded bg-gray-100 text-[0.85em]"
        >
          {m[4]}
        </code>
      );
    else if (m[5] != null)
      nodes.push(
        <a
          key={`${kp}l${i}`}
          href={m[6]}
          target="_blank"
          rel="noreferrer"
          className="text-[#0F3CBE] underline"
        >
          {m[5]}
        </a>
      );
    last = re.lastIndex;
    i++;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
};

const Markdown: React.FC<{ text: string }> = ({ text }) => {
  const lines = text.split("\n");
  const blocks: React.ReactNode[] = [];
  let i = 0;
  let key = 0;
  const isBullet = (s: string) => /^[-*]\s+/.test(s);
  const isOrdered = (s: string) => /^\d+\.\s+/.test(s);
  const isHeading = (s: string) => /^#{1,3}\s+/.test(s);

  while (i < lines.length) {
    const trimmed = lines[i].trim();
    if (trimmed === "") {
      i++;
      continue;
    }
    if (isBullet(trimmed)) {
      const items: string[] = [];
      while (i < lines.length && isBullet(lines[i].trim())) {
        items.push(lines[i].trim().replace(/^[-*]\s+/, ""));
        i++;
      }
      blocks.push(
        <ul key={`ul${key}`} className="list-disc pl-5 space-y-1 my-1">
          {items.map((it, k) => (
            <li key={k}>{renderInline(it, `ul${key}-${k}-`)}</li>
          ))}
        </ul>
      );
      key++;
      continue;
    }
    if (isOrdered(trimmed)) {
      const items: string[] = [];
      while (i < lines.length && isOrdered(lines[i].trim())) {
        items.push(lines[i].trim().replace(/^\d+\.\s+/, ""));
        i++;
      }
      blocks.push(
        <ol key={`ol${key}`} className="list-decimal pl-5 space-y-1 my-1">
          {items.map((it, k) => (
            <li key={k}>{renderInline(it, `ol${key}-${k}-`)}</li>
          ))}
        </ol>
      );
      key++;
      continue;
    }
    if (isHeading(trimmed)) {
      const h = trimmed.replace(/^#{1,3}\s+/, "");
      blocks.push(
        <p key={`h${key}`} className="font-semibold text-modalText mt-2 mb-1">
          {renderInline(h, `h${key}-`)}
        </p>
      );
      key++;
      i++;
      continue;
    }
    const para: string[] = [];
    while (
      i < lines.length &&
      lines[i].trim() !== "" &&
      !isBullet(lines[i].trim()) &&
      !isOrdered(lines[i].trim()) &&
      !isHeading(lines[i].trim())
    ) {
      para.push(lines[i].trim());
      i++;
    }
    blocks.push(
      <p key={`p${key}`} className="my-1 leading-relaxed">
        {renderInline(para.join(" "), `p${key}-`)}
      </p>
    );
    key++;
  }
  return <>{blocks}</>;
};

const TypingDots: React.FC = () => (
  <span className="inline-flex items-center gap-1 py-1" aria-label="Assistant is typing">
    {[0, 150, 300].map((d) => (
      <span
        key={d}
        className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce"
        style={{ animationDelay: `${d}ms` }}
      />
    ))}
  </span>
);

const PublicAIChat: React.FC = () => {
  const { hospital_id } = useParams<{ hospital_id: string }>();
  const hospitalId = hospital_id || "";

  // Carry ?public=1 across navigation ONLY when we're in local public mode
  // (the real public host serves clean URLs).
  const localPublic = useMemo(
    () =>
      typeof window !== "undefined" &&
      new URLSearchParams(window.location.search).get("public") === "1",
    []
  );
  const bookingHref = `/${hospitalId}${localPublic ? "?public=1" : ""}`;

  const [branding, setBranding] = useState<HospitalBranding | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);

  // Refs: latest messages (for history), abort handle, id counter, init guard.
  const messagesRef = useRef<ChatMessage[]>([]);
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);
  const abortRef = useRef<AbortController | null>(null);
  const idRef = useRef(0);
  const nextId = () => String(++idRef.current);
  const didInitRef = useRef(false);

  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [messages]);

  const buildHistory = (msgs: ChatMessage[]): AiTurn[] =>
    msgs
      .filter(
        (m) =>
          !m.excludeFromHistory &&
          m.status === "done" &&
          m.content.trim() !== ""
      )
      .slice(-HISTORY_TURNS)
      .map((m) => ({ role: m.role, content: m.content }));

  // Streams a response into an existing assistant bubble (by id).
  const streamInto = useCallback(
    async (assistantId: string, message: string, history: AiTurn[]) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setStreaming(true);
      setMessages((prev) =>
        prev.map((m) =>
          m.id === assistantId ? { ...m, content: "", status: "streaming" } : m
        )
      );

      try {
        await streamPublicAiAsk({
          hospitalId,
          message,
          history,
          signal: controller.signal,
          onChunk: (chunk) =>
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantId
                  ? { ...m, content: m.content + chunk }
                  : m
              )
            ),
        });
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantId ? { ...m, status: "done" } : m
          )
        );
      } catch (err: any) {
        if (err?.name === "AbortError") {
          // User stopped — keep whatever streamed so far.
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantId
                ? { ...m, status: m.content ? "done" : "error" }
                : m
            )
          );
        } else if (err instanceof AiAskError && err.status === 429) {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantId
                ? { ...m, status: "rate_limited", content: RATE_LIMIT_TEXT }
                : m
            )
          );
        } else {
          const msg =
            err instanceof AiAskError && err.message
              ? err.message
              : "Something went wrong. Please try again.";
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantId ? { ...m, status: "error", content: msg } : m
            )
          );
        }
      } finally {
        if (abortRef.current === controller) abortRef.current = null;
        setStreaming(false);
      }
    },
    [hospitalId]
  );

  const sendMessage = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || !hospitalId || streaming) return;
      const history = buildHistory(messagesRef.current);
      const assistantId = nextId();
      setMessages((prev) => [
        ...prev,
        { id: nextId(), role: "user", content: trimmed, status: "done" },
        {
          id: assistantId,
          role: "assistant",
          content: "",
          status: "streaming",
          retryMessage: trimmed,
        },
      ]);
      setInput("");
      void streamInto(assistantId, trimmed, history);
    },
    [hospitalId, streaming, streamInto]
  );

  const retry = useCallback(
    (assistantId: string) => {
      if (streaming) return;
      const msgs = messagesRef.current;
      const idx = msgs.findIndex((m) => m.id === assistantId);
      if (idx < 0) return;
      const target = msgs[idx];
      const isIntro = target.retryMessage === INTRO_SENTINEL;
      const message = isIntro ? INTRO_MESSAGE : target.retryMessage || "";
      const history = buildHistory(msgs.slice(0, idx));
      void streamInto(assistantId, message, history);
    },
    [streaming, streamInto]
  );

  // ── On mount: branding + the one-shot auto-highlights intro ────────────────
  useEffect(() => {
    if (!hospitalId) return;
    getPublicHospital(hospitalId)
      .then(setBranding)
      .catch(() => setBranding(null));
  }, [hospitalId]);

  useEffect(() => {
    if (!hospitalId || didInitRef.current) return;
    didInitRef.current = true;
    const introId = nextId();
    setMessages([
      {
        id: introId,
        role: "assistant",
        content: "",
        status: "streaming",
        excludeFromHistory: true,
        retryMessage: INTRO_SENTINEL,
      },
    ]);
    void streamInto(introId, INTRO_MESSAGE, []);
    // streamInto is stable per hospitalId; intro runs exactly once per mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hospitalId]);

  // Clean up any in-flight stream on unmount.
  useEffect(() => () => abortRef.current?.abort(), []);

  const handleStop = () => abortRef.current?.abort();

  const onInputKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  // Suggested chips show only before the patient has asked anything.
  const hasUserMessage = messages.some((m) => m.role === "user");

  const hospitalName = branding?.hospital_name || "Our Hospital";

  return (
    <div className="min-h-screen h-screen flex flex-col bg-gray-50 md:bg-gradient-to-b md:from-[#EEF3FF] md:via-white md:to-[#F4F7FE]">
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <header className="shrink-0 bg-white border-b border-doctorsBorder">
        <div className="max-w-3xl w-full mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#0F3CBE] to-[#3D5AE0] text-white font-bold flex items-center justify-center flex-shrink-0">
              {hospitalName.charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0">
              <p className="text-modalText font-semibold text-sm sm:text-base truncate leading-tight">
                {hospitalName}
              </p>
              <p className="text-[11px] sm:text-xs text-queueText leading-tight">
                AI Assistant
              </p>
            </div>
          </div>
          <Link
            to={bookingHref}
            className="shrink-0 text-xs sm:text-sm font-semibold px-3 sm:px-4 py-2 rounded-lg bg-[#0F3CBE] text-white hover:bg-[#0d32a1] whitespace-nowrap"
          >
            Book Appointment
          </Link>
        </div>
      </header>

      {/* ── Messages ────────────────────────────────────────────────────── */}
      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto">
        <div className="max-w-3xl w-full mx-auto px-4 sm:px-6 py-5 space-y-4">
          {messages.map((m) => (
            <div
              key={m.id}
              className={`flex ${
                m.role === "user" ? "justify-end" : "justify-start"
              }`}
            >
              <div
                className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm ${
                  m.role === "user"
                    ? "bg-[#0F3CBE] text-white rounded-br-sm"
                    : m.status === "error"
                    ? "bg-red-50 text-red-700 border border-red-200 rounded-bl-sm"
                    : m.status === "rate_limited"
                    ? "bg-amber-50 text-amber-800 border border-amber-200 rounded-bl-sm"
                    : "bg-white text-modalText border border-doctorsBorder rounded-bl-sm"
                }`}
              >
                {m.role === "assistant" &&
                m.status === "streaming" &&
                m.content === "" ? (
                  <TypingDots />
                ) : m.role === "user" ? (
                  <p className="whitespace-pre-wrap">{m.content}</p>
                ) : (
                  <div className="space-y-1">
                    <Markdown text={m.content} />
                    {m.status === "error" && (
                      <button
                        onClick={() => retry(m.id)}
                        disabled={streaming}
                        className="mt-1 text-xs font-semibold text-red-700 underline disabled:opacity-50"
                      >
                        Retry
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}

          {/* Suggested chips — under the intro, until the first question. */}
          {!hasUserMessage && (
            <div className="flex flex-wrap gap-2 pt-1">
              {SUGGESTED_CHIPS.map((c) => (
                <button
                  key={c}
                  onClick={() => sendMessage(c)}
                  disabled={streaming}
                  className="text-xs sm:text-sm font-medium px-3 py-1.5 rounded-full bg-white border border-doctorsBorder text-[#0F3CBE] hover:bg-[#EAF0FF] disabled:opacity-50"
                >
                  {c}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Input + disclaimer ──────────────────────────────────────────── */}
      <div className="shrink-0 bg-white border-t border-doctorsBorder">
        <div className="max-w-3xl w-full mx-auto px-4 sm:px-6 py-3">
          <div className="flex items-end gap-2">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onInputKeyDown}
              rows={1}
              placeholder="Ask about doctors, timings, services…"
              disabled={streaming}
              className="flex-1 resize-none max-h-32 px-3 py-2.5 text-sm rounded-xl border border-gray-300 focus:border-[#0F3CBE] focus:outline-none disabled:bg-gray-50 disabled:text-queueText"
            />
            {streaming ? (
              <button
                onClick={handleStop}
                className="shrink-0 text-sm font-semibold px-4 py-2.5 rounded-xl bg-gray-100 text-gray-700 border border-gray-300 hover:bg-gray-200"
              >
                Stop
              </button>
            ) : (
              <button
                onClick={() => sendMessage(input)}
                disabled={!input.trim()}
                className="shrink-0 text-sm font-semibold px-4 py-2.5 rounded-xl bg-[#0F3CBE] text-white hover:bg-[#0d32a1] disabled:opacity-50"
              >
                Send
              </button>
            )}
          </div>
          <p className="text-[10px] sm:text-[11px] text-queueText text-center mt-2">
            AI-generated — general info only, not medical advice.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PublicAIChat;
